import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';

type ChannelTag = Database['public']['Tables']['channel_tags']['Row'];

export interface TagWithChannelCount extends ChannelTag {
  channel_count: number;
}

export interface ChannelWithTags {
  channel_id: number;
  channel_name: string;
  tags: Pick<ChannelTag, 'id' | 'name'>[];
}

// Get all tags for a specific provider/version/region
export async function getTags(
  providerId: number,
  versionId: number,
  regionId: number
): Promise<TagWithChannelCount[]> {
  // Use raw SQL for reliable counting with proper typing
  const { data, error } = await supabase.rpc(
    'get_tags_with_counts' as any, 
    {
      p_provider_id: providerId,
      p_version_id: versionId,
      p_region_id: regionId
    }
  ) as { data: TagWithChannelCount[] | null; error: any };

  if (error) {
    console.warn('RPC query failed, using fallback:', error);
    return await getTagsWithManualCount(providerId, versionId, regionId);
  }

  return (data as TagWithChannelCount[]) || [];
}

// Fallback function that manually counts channel assignments
async function getTagsWithManualCount(
  providerId: number,
  versionId: number,
  regionId: number
): Promise<TagWithChannelCount[]> {
  // Get all tags
  const { data: tags, error: tagsError } = await supabase
    .from('channel_tags')
    .select('*')
    .eq('provider_id', providerId)
    .eq('version_id', versionId)
    .eq('region_id', regionId)
    .order('name');

  if (tagsError) throw tagsError;

  // Get channel counts for each tag
  const tagsWithCounts = await Promise.all(
    (tags || []).map(async (tag) => {
      const { count, error: countError } = await supabase
        .from('channel_tag_assignments')
        .select('*', { count: 'exact', head: true })
        .eq('tag_id', tag.id);

      if (countError) {
        console.warn(`Failed to count channels for tag ${tag.id}:`, countError);
        return { ...tag, channel_count: 0 };
      }

      return { ...tag, channel_count: count || 0 };
    })
  );

  return tagsWithCounts;
}

// Get tags assigned to specific channels
export async function getChannelTags(
  channelIds: number[]
): Promise<ChannelWithTags[]> {
  if (channelIds.length === 0) return [];

  const { data, error } = await supabase
    .from('channels')
    .select(`
      id,
      name,
      channel_tag_assignments(
        channel_tags(
          id,
          name
        )
      )
    `)
    .in('id', channelIds);

  if (error) throw error;

  return (data || []).map(channel => ({
    channel_id: channel.id,
    channel_name: channel.name,
    tags: (channel.channel_tag_assignments || [])
      .map(assignment => assignment.channel_tags)
      .filter(Boolean)
      .map(tag => ({
        id: tag!.id,
        name: tag!.name
      }))
  }));
}

// Get all channels for a specific tag
export async function getTagChannels(
  tagId: number,
  providerId: number,
  versionId: number
): Promise<{ id: number; name: string }[]> {
  const { data, error } = await supabase
    .from('channel_tag_assignments')
    .select(`
      channels(
        id,
        name
      )
    `)
    .eq('tag_id', tagId)
    .eq('channels.provider_id', providerId)
    .eq('channels.version_id', versionId);

  if (error) throw error;

  return (data || [])
    .map(assignment => assignment.channels)
    .filter(Boolean)
    .map(channel => ({
      id: channel!.id,
      name: channel!.name
    }));
}

// Search tags by name
export async function searchTags(
  providerId: number,
  versionId: number,
  regionId: number,
  searchTerm: string
): Promise<ChannelTag[]> {
  const { data, error } = await supabase
    .from('channel_tags')
    .select('*')
    .eq('provider_id', providerId)
    .eq('version_id', versionId)
    .eq('region_id', regionId)
    .ilike('name', `%${searchTerm}%`)
    .order('name')
    .limit(20);

  if (error) throw error;
  return data || [];
}